import { Button } from '@/components/Button'
import {
  Section,
  SectionItem,
} from '@/features/jobs/info-gathering/components/Section'
import { PhysicalAddressSectionItems } from '@/features/jobs/info-gathering/components/PhysicalAddressSectionItems'
import { type PhysicalAddress } from '@/features/jobs/info-gathering/PhysicalAddress'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/components/EmptyState'
import type {
  TaxRefundDeliveryMethodDisplay,
  TaxRefundMethodDisplay,
} from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodViewModel'

export interface RefundInfoProps {
  isBothEmpty: boolean
  isLoading: boolean
  isEditable: boolean
  hasPersonalSupportingItem: boolean
  hasBusinessSupportingItem: boolean
  isPersonalRefundMethodProvided: boolean
  isBusinessRefundMethodProvided: boolean
  personalRefundMethod: TaxRefundMethodDisplay | null
  businessRefundMethod: TaxRefundMethodDisplay | null
  onEditPersonal?: () => void
  onEditBusiness?: () => void
  emptyStateActionTestId?: string
  personalEditButtonTestId?: string
  personalEmptyStateActionTestId?: string
  businessEditButtonTestId?: string
  businessEmptyStateActionTestId?: string
}

export function RefundInfo({
  isBothEmpty,
  isLoading,
  isEditable,
  hasPersonalSupportingItem,
  hasBusinessSupportingItem,
  isPersonalRefundMethodProvided,
  isBusinessRefundMethodProvided,
  personalRefundMethod,
  businessRefundMethod,
  onEditPersonal,
  onEditBusiness,
  emptyStateActionTestId,
  personalEditButtonTestId,
  personalEmptyStateActionTestId,
  businessEditButtonTestId,
  businessEmptyStateActionTestId,
}: RefundInfoProps) {
  return isBothEmpty && isEditable ? (
    <EmptyState size="section">
      <EmptyStateTitle>No refund info yet</EmptyStateTitle>
      <EmptyStateDescription>
        Fill in the client's details on their behalf.
      </EmptyStateDescription>
      <EmptyStateActions>
        <Button
          label="Fill in refund info"
          variant="text"
          onPress={onEditPersonal}
          testId={emptyStateActionTestId}
        />
      </EmptyStateActions>
    </EmptyState>
  ) : (
    <>
      {hasPersonalSupportingItem && (
        <>
          {isPersonalRefundMethodProvided ? (
            <Section
              title="Personal tax refund"
              bordered
              isLoading={isLoading}
              actions={
                isEditable ? (
                  <Button
                    label="Edit"
                    variant="text"
                    onPress={onEditPersonal}
                    testId={personalEditButtonTestId}
                  />
                ) : undefined
              }
            >
              <SectionItem
                label="Method"
                value={personalRefundMethod?.deliveryMethodType}
              />
              <RefundMethodSection
                deliveryMethod={personalRefundMethod?.deliveryMethod ?? null}
              />
            </Section>
          ) : (
            isEditable && (
              <EmptyState size="section">
                <EmptyStateTitle>No personal refund info yet</EmptyStateTitle>
                <EmptyStateDescription>
                  Fill in the client's details on their behalf.
                </EmptyStateDescription>
                <EmptyStateActions>
                  <Button
                    label="Fill in personal refund info"
                    variant="text"
                    onPress={onEditPersonal}
                    testId={personalEmptyStateActionTestId}
                  />
                </EmptyStateActions>
              </EmptyState>
            )
          )}
        </>
      )}
      {hasBusinessSupportingItem && (
        <>
          {isBusinessRefundMethodProvided ? (
            <Section
              title="Business tax refund"
              bordered
              isLoading={isLoading}
              actions={
                isEditable ? (
                  <Button
                    label="Edit"
                    variant="text"
                    onPress={onEditBusiness}
                    testId={businessEditButtonTestId}
                  />
                ) : undefined
              }
            >
              <SectionItem
                label="Method"
                value={businessRefundMethod?.deliveryMethodType}
              />
              <RefundMethodSection
                deliveryMethod={businessRefundMethod?.deliveryMethod ?? null}
              />
            </Section>
          ) : (
            isEditable && (
              <EmptyState size="section">
                <EmptyStateTitle>No business refund info yet</EmptyStateTitle>
                <EmptyStateDescription>
                  Fill in the client's details on their behalf.
                </EmptyStateDescription>
                <EmptyStateActions>
                  <Button
                    label="Fill in business refund info"
                    variant="text"
                    onPress={onEditBusiness}
                    testId={businessEmptyStateActionTestId}
                  />
                </EmptyStateActions>
              </EmptyState>
            )
          )}
        </>
      )}
    </>
  )
}

interface RefundMethodSectionProps {
  deliveryMethod: TaxRefundDeliveryMethodDisplay | null
}

function RefundMethodSection({ deliveryMethod }: RefundMethodSectionProps) {
  return (
    <>
      {deliveryMethod?.type === 'Check' && (
        <CheckTaxRefundDeliveryMethodSection
          mailingAddress={deliveryMethod.mailingAddress}
          sameAddressAs={deliveryMethod.sameAddressAs}
        />
      )}

      {deliveryMethod?.type === 'DirectDeposit' && (
        <DirectDepositTaxRefundDeliveryMethodSection
          bankAccount={deliveryMethod}
        />
      )}
    </>
  )
}

interface CheckTaxRefundDeliveryMethodSectionProps {
  mailingAddress: PhysicalAddress | null
  sameAddressAs: 'personal' | 'business' | null
}

function CheckTaxRefundDeliveryMethodSection({
  mailingAddress,
  sameAddressAs,
}: CheckTaxRefundDeliveryMethodSectionProps) {
  if (mailingAddress) {
    return <PhysicalAddressSectionItems address={mailingAddress} />
  }

  if (sameAddressAs === 'personal') {
    return (
      <SectionItem
        label="Address"
        value="Using same address as personal information."
        copyable={false}
      />
    )
  }

  if (sameAddressAs === 'business') {
    return (
      <SectionItem
        label="Address"
        value="Using same address as business information."
        copyable={false}
      />
    )
  }

  return null
}

interface DirectDepositTaxRefundDeliveryMethodSectionProps {
  bankAccount: Extract<
    TaxRefundDeliveryMethodDisplay,
    { type: 'DirectDeposit' }
  >
}

function DirectDepositTaxRefundDeliveryMethodSection({
  bankAccount,
}: DirectDepositTaxRefundDeliveryMethodSectionProps) {
  return (
    <>
      <SectionItem
        label="Account type"
        value={bankAccount.accountType}
        isSensitive
      />

      <SectionItem
        label="Routing number"
        value={bankAccount.routingNumber}
        maskedValue={bankAccount.maskedRoutingNumber}
        isSensitive
      />

      <SectionItem
        label="Account number"
        value={bankAccount.accountNumber}
        maskedValue={bankAccount.maskedAccountNumber}
        isSensitive
      />

      <SectionItem
        label="Bank name"
        value={bankAccount.bankName}
        maskedValue={bankAccount.maskedBankName}
        isSensitive
        showHideLabel="name"
      />
    </>
  )
}
